/* Debounce helper (kept same as original) */
if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

/**
 * -------------------------------------------------------------------
 *  THEME HEADER (Optimized for Zero Forced Reflows)
 * -------------------------------------------------------------------
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.ticking = false;
    }

    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      /* -------------------------------------------
       * ESC key closes the mobile menu
       * ------------------------------------------- */
      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.toggle.removeAttribute('open');
          this.toggle.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
        }
      });

      /* -------------------------------------------
       * Mobile Toggle Button
       * ------------------------------------------- */
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      /* -------------------------------------------
       * Combined Optimized Scroll Handler
       * ------------------------------------------- */
      window.addEventListener('scroll', this.handleScrollOptimized.bind(this), { passive: true });

      // Trigger initial scroll position
      window.dispatchEvent(new Event('scroll'));

      /* -------------------------------------------
       * Announcement height setup
       * ------------------------------------------- */
      if (document.querySelector('.announcement-bar-section')) {
        this.setAnnouncementHeight();
        window.addEventListener('resize', debounce(() => this.setAnnouncementHeight(), 100));
        window.dispatchEvent(new Event('resize'));
      }

      /* -------------------------------------------
       * Mobile Menu Events
       * ------------------------------------------- */
      this.menu.querySelectorAll('summary')
        .forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));

      this.menu.querySelectorAll('.parent-link-back--button')
        .forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    /* -------------------------------------------------------------------
     * Optimized scroll handler — ZERO forced reflows
     * ------------------------------------------------------------------- */
    handleScrollOptimized() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.runScrollUpdates();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    runScrollUpdates() {
      const scrollY = window.scrollY;

      // Batch DOM reads
      const headerTop = this.header_section.getBoundingClientRect().top;
      const headerHeight = this.clientHeight;
      const stickyOffset =
        parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;

      // -------------------------------------------
      // Sticky header logic (unchanged)
      // -------------------------------------------
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', scrollY >= stickyOffset && scrollY > 0);
      }

      // -------------------------------------------
      // CSS properties (write only)
      // -------------------------------------------
      document.documentElement.style.setProperty('--header-offset', headerTop + 'px');
      document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
    }

    /* -------------------------------------------------------------------
     * Announcement bar height
     * ------------------------------------------------------------------- */
    setAnnouncementHeight() {
      const a_bar = document.getElementById('shopify-section-announcement-bar');
      if (a_bar) {
        const h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }

    /* -------------------------------------------------------------------
     * Menu / Submenu logic (unchanged)
     * ------------------------------------------------------------------- */
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }

    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) animationStart = time;

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }

  customElements.define('theme-header', ThemeHeader);
}

/**
 * -------------------------------------------------------------------
 *  FULL MENU
 * -------------------------------------------------------------------
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.submenus =
        this.querySelectorAll('.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu');

      if (!this.submenus.length) return;

      // Resize on load
      window.addEventListener('resize', debounce(() => this.resizeSubMenus(), 100));
      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope > .menu-item-has-children > .sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          const w = sub_submenu.offsetWidth;
          const left =
            sub_submenu.parentElement.parentElement.getBoundingClientRect().left +
            sub_submenu.parentElement.parentElement.clientWidth +
            10;

          const total = w + left;

          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}
